import { SITE_CONFIG } from "../app/config";

const BASE_URL = `https://${SITE_CONFIG.domain}`;

/**
 * JSON-LD structured data builders for programmatic SEO pages.
 * Each function returns a plain object ready for JSON.stringify.
 */

export const buildWebsiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_CONFIG.name,
  url: BASE_URL,
  description:
    "Turn your Spotify playlist into a vinyl & CD shopping list. Own your music collection forever.",
  potentialAction: {
    "@type": "SearchAction",
    target: `${BASE_URL}/?playlist={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
});

export const buildOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_CONFIG.companyName,
  url: SITE_CONFIG.companyUrl,
  brand: {
    "@type": "Brand",
    name: SITE_CONFIG.name,
  },
});

type DefinedTermInput = {
  term: string;
  definition: string;
  slug: string;
  category: string;
};

export const buildDefinedTermSchema = ({ term, definition, slug, category }: DefinedTermInput) => ({
  "@context": "https://schema.org",
  "@type": "DefinedTerm",
  name: term,
  description: definition,
  url: `${BASE_URL}/glossary/${slug}`,
  inDefinedTermSet: {
    "@type": "DefinedTermSet",
    name: `${SITE_CONFIG.name} Music Glossary`,
    url: `${BASE_URL}/glossary`,
  },
  termCode: category,
});

type ArticleInput = {
  title: string;
  description: string;
  path: string;
  datePublished: string;
  dateModified?: string;
};

export const buildArticleSchema = ({ title, description, path, datePublished, dateModified }: ArticleInput) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: title,
  description,
  url: `${BASE_URL}${path}`,
  datePublished,
  dateModified: dateModified ?? datePublished,
  author: {
    "@type": "Organization",
    name: SITE_CONFIG.companyName,
  },
  publisher: {
    "@type": "Organization",
    name: SITE_CONFIG.name,
    url: BASE_URL,
  },
});

type BreadcrumbItem = {
  name: string;
  url: string;
};

export const buildBreadcrumbSchema = (items: BreadcrumbItem[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: item.url.startsWith("http") ? item.url : `${BASE_URL}${item.url}`,
  })),
});

type FAQItem = {
  question: string;
  answer: string;
};

export const buildFAQSchema = (items: FAQItem[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  name: "Frequently Asked Questions — GoOffline",
  mainEntity: items.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  })),
});

type ComparisonInput = {
  title: string;
  description: string;
  path: string;
  itemA: string;
  itemB: string;
};

export const buildComparisonSchema = ({ title, description, path, itemA, itemB }: ComparisonInput) => [
  buildArticleSchema({
    title,
    description,
    path,
    datePublished: "2025-01-15",
    dateModified: "2026-01-15",
  }),
  buildBreadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Compare", url: "/compare" },
    { name: `${itemA} vs ${itemB}`, url: path },
  ]),
];

type PersonaInput = {
  title: string;
  description: string;
  path: string;
  persona: string;
};

export const buildPersonaSchema = ({ title, description, path, persona }: PersonaInput) => [
  buildArticleSchema({
    title,
    description,
    path,
    datePublished: "2025-01-15",
    dateModified: "2026-01-15",
  }),
  buildBreadcrumbSchema([
    { name: "Home", url: "/" },
    { name: persona, url: path },
  ]),
];

type CurationInput = {
  title: string;
  description: string;
  path: string;
};

export const buildCurationSchema = ({ title, description, path }: CurationInput) => [
  buildArticleSchema({
    title,
    description,
    path,
    datePublished: "2025-06-01",
    dateModified: "2026-02-01",
  }),
  buildBreadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Best Of", url: "/best" },
    { name: title, url: path },
  ]),
];

type GuideInput = {
  title: string;
  description: string;
  path: string;
  steps: string[];
};

export const buildGuideSchema = ({ title, description, path, steps }: GuideInput) => [
  {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: title,
    description,
    url: `${BASE_URL}${path}`,
    step: steps.map((stepName, index) => ({
      "@type": "HowToStep",
      position: index + 1,
      name: stepName,
    })),
    author: {
      "@type": "Organization",
      name: SITE_CONFIG.companyName,
    },
  },
  buildBreadcrumbSchema([
    { name: "Home", url: "/" },
    { name: "Guides", url: "/guides" },
    { name: title, url: path },
  ]),
];
